import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Line,
  ReferenceArea,
  ReferenceLine,
  Area,
  ComposedChart,
  LineChart,
  BarChart,
  Bar,
  Pie,
  PieChart,
  Cell,
  Legend,
} from 'recharts';
import _ from 'lodash';

import {
  CustomizedXAxisTickIrregularMinMaxOnly,
  CustomizedXAxisTickNumeric,
  CustomizedYAxisTickNumeric,
} from './GraphAxes';
import { CustomizedYAxisTick_Category, CustomReferenceArea } from './GraphComponents';
import { DATE_FORMAT_LLLL } from '../../constants';
import {
  CONDENSED_VIEW_THRESHOLD,
  GRAPH_ROW_BK,
  GRAPH_ROW_BK_ALTERNATE,
  GRAPH_DOT_RADIUS,
  GRAPH_DOT_RADIUS_CONDENSED,
  GRAPH_ACTIVE_DOT_RADIUS,
  GRAPH_AXIS_FONT_SIZE,
  GRAPH_AXIS_X_HEIGHT,
  GRAPH_AXIS_X_HEIGHT_CONDENSED,
  GRAPH_AXIS_LINE_COLOR,
  GRAPH_AXIS_LINE_WIDTH,
  GRAPH_REF_LINE_COLOR,
  GRAPH_REF_LINE_DASH,
  GRAPH_Y_AXIS_FONT_COLOR,
} from './GraphConstants';
import './graph.scss';
import { manipulateColor } from './CompositeGraph/ColorTools';

const Y_AXIS_TOP_PADDING = 2;
const DEFAULT_LABEL_CHAR_WIDTH = GRAPH_AXIS_FONT_SIZE * 0.6;

function measureXAxisLabelWidth(label) {
  const text = String(label || '');
  if (typeof document === 'undefined') {
    return text.length * DEFAULT_LABEL_CHAR_WIDTH;
  }

  const canvas = measureXAxisLabelWidth.canvas || (measureXAxisLabelWidth.canvas = document.createElement('canvas'));
  const context = canvas.getContext('2d');
  if (!context) {
    return text.length * DEFAULT_LABEL_CHAR_WIDTH;
  }

  context.font = `${GRAPH_AXIS_FONT_SIZE}px sans-serif`;
  return context.measureText(text).width;
}

function GetSeries_All(data) {
  const ret = [];
  data.forEach(category => {
    ret.push({
      x: category.x,
      y: category.values[0],
      all: category,
    });
  });

  return ret;
}

function GetSeries(data, index) {
  const ret = [];
  data.forEach(day => {
    ret.push({
      x: day.x,
      y: day.values[index],
    });
  });

  return ret;
}

function TooltipMetricRow({ label, value, isSummary = false }) {
  return (
    <div className={isSummary ? 'customTooltipDescrSummary' : 'customTooltipDescr'}>
      <span className="customTooltipDescrLabel">{label}</span>
      <span className="customTooltipDescrValue">{value}</span>
    </div>
  );
}

TooltipMetricRow.propTypes = {
  isSummary: PropTypes.bool,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

const GetSeries_Cat = (dss, chartData, index) => {
  return dss.map(ds => ({
    x: ds.name,
    y: chartData[index][ds.name],
    catName: chartData[index].x,
    values: chartData[index].values,
  }));
};

const GetSeries_Conditional = (dss, chartData, condition, catName, level, dataParents) => {
  const data = chartData.filter(d => condition(d));
  const ret = [];

  dss.forEach(ds => {
    data.forEach((d, index) => {
      const siblingsCount = !d.parent ? 0 : data.filter(d2 => d2.parent === d.parent).length;
      const siblingIndex = !d.parent ? 0 : data.filter(d2 => d2.parent === d.parent).findIndex(d2 => d2.x === d.x);
      const colorShift = !d.parent ? 0 : (siblingIndex + 1) / (siblingsCount + 1);
      const colorShiftSoftened = !d.parent ? 0 : colorShift * 0.7;
      const entry = {
        ds: ds.name,
        x: d.x + ' - ' + ds.name,
        y: d[ds.name],
        catName,
        refName: d.x,
        level,
        values: d.values,
        color:
          level === 0
            ? ds.colors[index]
            : manipulateColor(colorShiftSoftened, ds.colors[dataParents.findIndex(p => p.refName === d.parent)]),
      };
      ret.push(entry);
    });
  });

  return ret;
};

function CustomCategoryTooltip(props) {
  const { active, payload, dataSeries } = props;
  // console.log(payload, props);
  if (active && payload && payload.length > 0) {
    if (payload[0].payload.values !== undefined) {
      const sum = payload[0].payload.values.filter(v => !isNaN(v)).reduce((partialSum, a) => partialSum + a, 0);
      if (payload[0].payload.catName !== undefined) {
        return (
          <div className="customTooltip">
            <p className="customTooltipTitle">{payload[0].payload.catName}</p>
            {dataSeries.map(
              (ds, i) =>
                !isNaN(payload[0].payload.values[i]) && (
                  <TooltipMetricRow key={ds.name} label={ds.name} value={payload[0].payload.values[i]} />
                ),
            )}
            {dataSeries.length > 1 && <TooltipMetricRow isSummary label="All" value={sum} />}
          </div>
        );
      }

      if (payload[0].payload.x !== undefined) {
        return (
          <div className="customTooltip">
            <p className="customTooltipTitle">{payload[0].payload.x}</p>
            {dataSeries.map(
              (ds, i) =>
                !isNaN(payload[0].payload.values[i]) && (
                  <TooltipMetricRow key={ds.name} label={ds.name} value={payload[0].payload.values[i]} />
                ),
            )}
            {dataSeries.length > 1 && <TooltipMetricRow isSummary label="All" value={sum} />}
          </div>
        );
      }
    }

    if (payload[0].payload.x !== undefined && !payload[0].payload.values) {
      const sum = payload.reduce((partialSum, a) => partialSum + a.value, 0);
      return (
        <div className="customTooltip">
          <p className="customTooltipTitle">{payload[0].payload.x}</p>
          {payload.map(p => (
            <TooltipMetricRow key={`customTT_${p.name}`} label={p.name} value={p.value} />
          ))}
          {payload.length > 1 && <TooltipMetricRow isSummary label="All" value={sum} />}
        </div>
      );
    }

    // if (payload[0].payload.values == null) return null; // only valid pp's
    // const sum = payload[0].payload.values.reduce((partialSum, a) => partialSum + a, 0);
    // return (
    //   <div className="customTooltip">
    //     <p className="customTooltipTitle">{tooltipTitle}</p>
    //     <div className="customTooltipDescr">{payload[0].payload.x}</div>
    //     {dataSeries.map((ds, index) => (
    //       <div className="customTooltipDescr" key={`customTT_${ds.name}`}>
    //         {ds.name}: {payload[0].payload.values[index] + (yAxisUnit ? ' ' + yAxisUnit : '')}
    //       </div>
    //     ))}
    //     {dataSeries.length > 1 && <div className="customTooltipDescr">All: {sum}</div>}
    //   </div>
    // );

    // if (payload.length > 1) {
    //   // only valid pp's
    //   return (
    //     <div className="customTooltip">
    //       <p className="customTooltipTitle">{tooltipTitle}</p>
    //       {payload.map(pp => (
    //         <div key={`customTT_${pp.name}`} className="customTooltipDescr">{`${pp.name}: ${pp.value}`}</div>
    //       ))}
    //     </div>
    //   );
    // }
  }
  return null;
}

function CustomTooltip({ active, payload, formatter, tooltipTitle }) {
  // console.log('payload', payload);
  if (active && payload && payload.length > 0) {
    if (formatter) {
      const formattedRows = payload
        .map((p, index) => formatter(payload[0].payload.x, index, p.value ?? p.payload?.y))
        .filter(Boolean);

      if (formattedRows.length > 0) {
        return (
          <div className="customTooltip">
            <p className="customTooltipTitle">{tooltipTitle}</p>
            {formattedRows.map((row, index) => {
              if (row.lines?.length) {
                return row.lines.map((line, lineIndex) => (
                  <div className="customTooltipDescr" key={`customTT_${index}_${lineIndex}`}>
                    {line}
                  </div>
                ));
              }
              return (
                <div className="customTooltipDescr" key={`customTT_${index}`}>
                  {row.text}
                </div>
              );
            })}
          </div>
        );
      }
    }

    const tooltipLabel = payload[0]?.payload?.x;
    const tooltipRows = payload
      .map((p, index) => ({
        key: `customTT_${p.name || index}`,
        label: p.name || 'Value',
        value: p.value ?? p.payload?.y,
      }))
      .filter(row => row.value !== undefined && row.value !== null && !isNaN(row.value));

    if (tooltipRows.length > 0) {
      const sum = tooltipRows.reduce((partialSum, row) => partialSum + row.value, 0);
      return (
        <div className="customTooltip">
          {tooltipLabel !== undefined && <p className="customTooltipTitle">{tooltipLabel}</p>}
          {tooltipRows.map(row => (
            <TooltipMetricRow key={row.key} label={row.label} value={row.value} />
          ))}
          {tooltipRows.length > 1 && <TooltipMetricRow isSummary label="All" value={sum} />}
        </div>
      );
    }
  }
  return null;
}

function CustomHierarchicalTooltip(props) {
  const { active, payload, dataSeries, chartData } = props;

  if (active && payload && payload.length > 0 && dataSeries && chartData) {
    const level = payload[0].payload.level;
    const catName = payload[0].payload.catName;
    const chartDataFiltered = chartData.filter(d => (level === 0 ? !d.parent : !!d.parent));
    // const uniqueParents = _.uniq(chartDataFiltered.map(d => d.parent));

    // const data = [];

    // chartDataFiltered.forEach(d => {
    //   dataSeries.forEach(ds => {
    //     const entry = {
    //       cat: d.x,
    //       label: d.x + ' - ' + ds.name,
    //       value: d[ds.name],
    //     };
    //     data.push(entry);
    //   })
    // });

    return (
      <div className="customTooltip">
        <p className="customTooltipTitle">{catName}</p>
        {level === 0 &&
          chartDataFiltered.map((d, i) => (
            <React.Fragment key={d.x}>
              <div className={i === 0 ? 'customTooltipDescr' : 'customTooltipDescrSummary'}>{d.x}</div>
              {dataSeries.map(ds => (
                <div key={ds.name} className="customTooltipDescr">
                  {ds.name}: {d[ds.name]}
                </div>
              ))}
            </React.Fragment>
          ))}
        {level > 0 &&
          dataSeries.map((ds, i) => (
            <React.Fragment key={ds.name}>
              <div className={i === 0 ? 'customTooltipDescr' : 'customTooltipDescrSummary'}>
                {} {ds.name}
              </div>
              {chartDataFiltered.map(d => (
                <div key={d.x} className="customTooltipDescr">
                  {d.x}: {d[ds.name]}
                </div>
              ))}
            </React.Fragment>
          ))}
      </div>
    );
  }

  return null;
}

export const chartTypeEnum = Object.freeze({
  line: 'line',
  area: 'area',
  stackedArea: 'stackedArea',
  bar: 'bar',
  stackedBar: 'stackedBar',
  horizontalBar: 'horizontalBar',
  horizontalStackedBar: 'horizontalStackedBar',
  pie: 'pie',
  stackedPie: 'stackedPie',
  hierarchicalPie: 'hierarchicalPie',
});

function getContrastYIQ(color) {
  let r, g, b;
  if (color.startsWith('#')) {
    r = parseInt(color.substring(1, 3), 16);
    g = parseInt(color.substring(3, 5), 16);
    b = parseInt(color.substring(5, 7), 16);
  }
  if (color.startsWith('rgb')) {
    const rgbValues = color.match(/\d+/g);
    r = parseInt(rgbValues[0], 10);
    g = parseInt(rgbValues[1], 10);
    b = parseInt(rgbValues[2], 10);
  }
  if (!r || !g || !b) return GRAPH_Y_AXIS_FONT_COLOR;
  var yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 128 ? GRAPH_Y_AXIS_FONT_COLOR : '#FFFFFF';
}

function CustomizedLabel(props) {
  const { dsIndex, formatter, index, value, x, y, width, height, vertical, dataSeries } = props;
  if (!value) return null;

  const backgroundColor = dataSeries?.[dsIndex]?.color || '#ffffff';
  const textColor = getContrastYIQ(backgroundColor);

  const texts = formatter ? formatter(index, dsIndex, value) : [];
  //console.log(props);

  if (vertical) {
    const dx = x + width / 2;
    const dy = y + height - 5;

    return (
      <text
        textAnchor="start"
        dominantBaseline="central"
        transform={`translate(${dx},${dy}) rotate(-90)`}
        fill={textColor}
      >
        {texts.text}
      </text>
    );

    // horizontal text with line breaks
    // return <text x={dx} y={dy} textAnchor='middle' dominantBaseline='central'>
    //   {texts.lines.map((labelText, i) => (
    //     <tspan
    //       key={`labelText-${i}`}
    //       dy={i === 0 || !vertical? '0' : '1.2em'}
    //       x={dx}
    //       y={dy}
    //       fill={GRAPH_Y_AXIS_FONT_COLOR}
    //       textAnchor='middle'
    //       dominantBaseline='central'>
    //         {labelText}
    //     </tspan>
    //   ))}
    // </text>;
  }
  const dx = x + 5;
  const dy = y + height / 2;

  return (
    <text x={dx} y={dy} fill={textColor} textAnchor="start" dominantBaseline="central">
      {texts.text}
    </text>
  );
}

function GenericCategoryGraph(props) {
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) {
      return undefined;
    }

    const updateWidth = () => setContainerWidth(element.clientWidth || 0);
    updateWidth();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateWidth);
      return () => window.removeEventListener('resize', updateWidth);
    }

    const observer = new ResizeObserver(entries => {
      const width = entries[0]?.contentRect?.width || element.clientWidth || 0;
      setContainerWidth(width);
    });
    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  const isStacked =
    props.graphType === chartTypeEnum.stackedBar ||
    props.graphType === chartTypeEnum.stackedArea ||
    props.graphType === chartTypeEnum.horizontalStackedBar ||
    props.graphType === chartTypeEnum.stackedPie;
  const isHierarchical = props.graphType === chartTypeEnum.hierarchicalPie;
  const PrepareData = (graphData, dataSeries, categorySelector) => {
    const data = [];

    graphData.forEach(item => {
      const category = categorySelector(item);

      const graphEntry = {
        x: category,
        values: [],
      };

      dataSeries.forEach(ds => {
        graphEntry.values.push(ds.selector(item));
      });

      data.push(graphEntry);
    });

    return data;
  };

  const PrepareData_Stacked = (graphData, dataSeries, categorySelector) => {
    const data = [];

    graphData.forEach(item => {
      const category = categorySelector(item);

      const graphEntry = {
        x: category,
        values: [],
      };

      dataSeries.forEach(ds => {
        graphEntry.values.push(ds.selector(item));
      });

      dataSeries.forEach(ds => {
        graphEntry[ds.name] = ds.selector(item);
      });

      data.push(graphEntry);
    });

    // console.log('graphData', graphData);
    // console.log('PrepareData_Stacked', data);
    return data;
  };

  const PrepareData_Hierarchical = (graphData, dataSeries, categorySelector) => {
    const data = [];

    graphData.forEach(item => {
      const category = categorySelector(item);

      const graphEntry = {
        x: category,
        values: [],
      };

      dataSeries.forEach(ds => {
        graphEntry.values.push(ds.selector(item));
      });

      dataSeries.forEach(ds => {
        graphEntry[ds.name] = ds.selector(item);
      });

      graphEntry.parent = item.parent;

      data.push(graphEntry);
    });

    // console.log('graphData', graphData);
    // console.log('PrepareData_Stacked', data);
    return data;
  };

  const calcValMinMax2 = (graphData, dataSelector, useZeroAsMin) => {
    const items = graphData
      .map(dataSelector)
      .filter(item => typeof item === 'number' && !isNaN(item) && isFinite(item));

    if (useZeroAsMin) items.push(0);
    if (items.length === 0) {
      return { min: 0, max: 0 };
    }

    return {
      min: Math.min(...items),
      max: Math.max(...items),
    };
  };
  const calcValMinMax = (graphData, dataSeries, useZeroAsMin) => {
    let result;

    if (!isStacked) {
      dataSeries.forEach(ds => {
        const localMinMax = calcValMinMax2(graphData, ds.selector, useZeroAsMin);
        if (result === undefined) {
          result = localMinMax;
        } else {
          if (result.min > localMinMax.min) result.min = localMinMax.min;
          if (result.max < localMinMax.max) result.max = localMinMax.max;
        }
      });
    } else {
      let max = 0;
      graphData.forEach(gd => {
        let sum = 0;
        dataSeries.forEach(ds => {
          const value = ds.selector(gd);
          sum += typeof value === 'number' && !isNaN(value) && isFinite(value) ? value : 0;
        });

        if (max < sum) max = sum;
      });
      result = { min: 0, max };
    }
    return result || { min: 0, max: 0 };
  };

  const calcTickFromDelta = valDelta => {
    const maxTicks = 12;
    const tick =
      valDelta < maxTicks
        ? 1
        : valDelta < maxTicks * 2
        ? 2
        : valDelta < maxTicks * 5
        ? 5
        : valDelta < maxTicks * 10
        ? 10
        : valDelta < maxTicks * 20
        ? 20
        : valDelta < maxTicks * 50
        ? 50
        : valDelta < maxTicks * 100
        ? 100
        : valDelta < maxTicks * 200
        ? 200
        : valDelta < maxTicks * 500
        ? 500
        : 1000;

    return tick;
  };

  const {
    graphData,
    graphType = chartTypeEnum.line,
    hasBorder,
    forceCondensed,
    xAxisTopPosition,
    xAxisLabelsMinMaxOnly,
    skipCategoryAxis,
    xAxisType = 'category',
    yAxisWidth,
    yAxisUnit,
    yAxisAlign = 'center',
    tooltipTitle,
    tooltipContentLines,
    tooltipDateFormat,
    dataSeries,
    referenceLines,
    categorySelector,
    hasReferenceAreas,
  } = props;
  const safeGraphData = graphData || [];
  const safeDataSeries = dataSeries || [];

  const useZeroAsMin =
    graphType === chartTypeEnum.horizontalBar ||
    graphType === chartTypeEnum.horizontalStackedBar ||
    graphType === chartTypeEnum.bar ||
    graphType === chartTypeEnum.stackedBar ||
    graphType === chartTypeEnum.area ||
    graphType === chartTypeEnum.stackedArea ||
    props.useZeroAsMin;

  const chartData = isHierarchical
    ? PrepareData_Hierarchical(safeGraphData, safeDataSeries, categorySelector)
    : !isStacked
    ? PrepareData(safeGraphData, safeDataSeries, categorySelector)
    : PrepareData_Stacked(safeGraphData, safeDataSeries, categorySelector);
  const hasGraphData = safeGraphData.length > 0;
  const hasDataSeries = safeDataSeries.length > 0;

  const valMinMax = calcValMinMax(safeGraphData, safeDataSeries, useZeroAsMin);
  const valDelta = valMinMax.max - valMinMax.min;
  const tick = calcTickFromDelta(valDelta);

  const yMin = valMinMax.min - (valMinMax.min % tick);
  const yMax = valMinMax.max - (valMinMax.max % tick) + tick;
  const yTicksCount = (yMax - yMin) / tick + 1;
  const yTicks = Array.from({ length: yTicksCount }, (_, i) => yMin + i * tick);

  const dateFormatForTooltip = tooltipDateFormat ? tooltipDateFormat : DATE_FORMAT_LLLL;
  const verticalBarRadius = props.barRadius ?? [6, 6, 0, 0];
  const horizontalBarRadius = props.horizontalBarRadius ?? [0, 6, 6, 0];

  // console.log('chartData', chartData);
  const xAxisFirstValue = hasGraphData ? chartData[0].x : undefined;
  const xAxisLastValue = hasGraphData ? chartData[chartData.length - 1].x : undefined;
  const estimatedMaxXAxisLabelWidth = useMemo(
    () => Math.max(...chartData.map(item => measureXAxisLabelWidth(item.x)), 0),
    [chartData],
  );
  const estimatedXAxisSlotWidth =
    xAxisType === 'category' && chartData.length > 0 ? Math.max((containerWidth - 20) / chartData.length, 1) : 0;
  const autoXAxisTickStep =
    xAxisType === 'category' && !skipCategoryAxis && !xAxisLabelsMinMaxOnly && estimatedXAxisSlotWidth > 0
      ? Math.max(1, Math.ceil((estimatedMaxXAxisLabelWidth + 12) / estimatedXAxisSlotWidth))
      : 1;
  const useAutoCondensedXAxis =
    autoXAxisTickStep > 1 && xAxisType === 'category' && !skipCategoryAxis && !xAxisLabelsMinMaxOnly;
  const xAxisInterval = useAutoCondensedXAxis && autoXAxisTickStep > 1 ? autoXAxisTickStep - 1 : 0;
  const xAxisTick = skipCategoryAxis ? (
    false
  ) : xAxisLabelsMinMaxOnly ? (
    <CustomizedXAxisTickIrregularMinMaxOnly
      valueFirst={xAxisFirstValue}
      valueLast={xAxisLastValue}
      isTop={xAxisTopPosition}
    />
  ) : (
    undefined
  );

  if (!hasGraphData || !hasDataSeries) return <></>;

  const drawXAxis = () => {
    return (
      <XAxis
        type={xAxisType}
        domain={xAxisType === 'number' ? [xAxisFirstValue, xAxisLastValue] : undefined}
        dataKey="x"
        allowDuplicatedCategory={false}
        height={
          skipCategoryAxis
            ? GRAPH_AXIS_LINE_WIDTH + 2
            : chartData.length > CONDENSED_VIEW_THRESHOLD ||
              forceCondensed ||
              xAxisLabelsMinMaxOnly ||
              useAutoCondensedXAxis
            ? GRAPH_AXIS_X_HEIGHT_CONDENSED
            : GRAPH_AXIS_X_HEIGHT
        }
        interval={xAxisInterval}
        name="x"
        orientation={xAxisTopPosition ? 'top' : 'bottom'}
        tickLine={false}
        tickSize={2}
        tick={xAxisTick}
        padding={{
          left: 10,
          right: 10,
        }}
        axisLine={{
          stroke: GRAPH_AXIS_LINE_COLOR,
          strokeWidth: GRAPH_AXIS_LINE_WIDTH,
        }}
      />
    );
  };

  const drawYAxis = useZeroAsMin => {
    return (
      <YAxis
        type="number"
        allowDataOverflow
        //dataKey="y"
        name="yAxis"
        tick={<CustomizedYAxisTickNumeric yMin={yMin} yMax={yMax} width={yAxisWidth} tick={tick} unit={yAxisUnit} />}
        domain={[useZeroAsMin ? 0 : yMin, yMax]}
        ticks={yTicks}
        interval={0}
        tickSize={0}
        width={yAxisWidth}
        axisLine={{
          stroke: GRAPH_AXIS_LINE_COLOR,
          strokeWidth: GRAPH_AXIS_LINE_WIDTH,
        }}
        padding={{
          top: Y_AXIS_TOP_PADDING,
          bottom: xAxisTopPosition ? 5 : 2,
        }}
      />
    );
  };

  const drawYAxis_Horizontal = () => {
    return (
      <YAxis
        type="category"
        dataKey="x"
        allowDuplicatedCategory={false}
        interval={0}
        name="y"
        width={skipCategoryAxis ? GRAPH_AXIS_LINE_WIDTH + 5 : yAxisWidth}
        tickLine={false}
        tickSize={0}
        tick={skipCategoryAxis ? false : <CustomizedYAxisTick_Category width={yAxisWidth} align={yAxisAlign} />}
        axisLine={{
          stroke: GRAPH_AXIS_LINE_COLOR,
          strokeWidth: GRAPH_AXIS_LINE_WIDTH,
        }}
        padding={{
          top: Y_AXIS_TOP_PADDING,
          bottom: xAxisTopPosition ? 5 : 2,
        }}
      />
    );
  };

  const drawXAxis_Horizontal = useZeroAsMin => {
    return (
      <XAxis
        type="number"
        allowDataOverflow
        name="xAxis"
        domain={[useZeroAsMin ? 0 : yMin, yMax]}
        ticks={yTicks}
        interval={0}
        tickSize={0}
        tickMargin={5}
        tick={
          <CustomizedXAxisTickNumeric
            height={GRAPH_AXIS_X_HEIGHT_CONDENSED}
            xMin={yMin}
            xMax={yMax}
            width={yAxisWidth}
            tick={tick}
            unit={yAxisUnit}
          />
        }
        height={GRAPH_AXIS_X_HEIGHT_CONDENSED}
        orientation={xAxisTopPosition ? 'top' : 'bottom'}
        axisLine={{
          stroke: GRAPH_AXIS_LINE_COLOR,
          strokeWidth: GRAPH_AXIS_LINE_WIDTH,
        }}
        padding={{
          left: 2,
          right: 2,
        }}
      />
    );
  };

  const drawTooltip = () => {
    return (
      <Tooltip
        content={<CustomTooltip formatter={props.labelFormatter} />}
        dataKey="values"
        tooltipTitle={tooltipTitle}
      />
    );
  };
  const drawCategoryTooltip = () => {
    return <Tooltip content={<CustomCategoryTooltip dataSeries={dataSeries} />} dataKey="values" />;
  };
  const drawHierarchicalTooltip = () => {
    return (
      <Tooltip content={<CustomHierarchicalTooltip chartData={chartData} dataSeries={dataSeries} />} dataKey="values" />
    );
  };

  /* reference areas to mimic alternate rows */
  const drawAlternateRows = () => {
    return Array.from({ length: yTicksCount }, (_, i) => i).map(i => (
      <ReferenceArea
        x1={graphData[0].x}
        x2={graphData[graphData.length - 1].x}
        y1={i === 0 ? yMin : yMin + tick / 2 + (i - 1) * tick}
        y2={i !== yTicksCount - 1 ? yMin + tick / 2 + i * tick : yMin + i * tick}
        stroke={i % 2 === 0 ? GRAPH_ROW_BK : GRAPH_ROW_BK_ALTERNATE}
        fill={i % 2 === 0 ? GRAPH_ROW_BK : GRAPH_ROW_BK_ALTERNATE}
        key={`refarea${i}`}
        shape={
          <CustomReferenceArea
            dontExtend={false}
            //padding={X_AXIS_PADDING}
            padding={4}
          />
        }
      />
    ));
  };

  /* reference areas to mimic alternate cols */
  const drawAlternateCols = () => {
    return Array.from({ length: yTicksCount }, (_, i) => i).map(i => (
      <ReferenceArea
        y1={graphData[0].x}
        y2={graphData[graphData.length - 1].x}
        x1={i === 0 ? yMin : yMin + tick / 2 + (i - 1) * tick}
        x2={i !== yTicksCount - 1 ? yMin + tick / 2 + i * tick : yMin + i * tick}
        stroke={i % 2 === 0 ? GRAPH_ROW_BK : GRAPH_ROW_BK_ALTERNATE}
        fill={i % 2 === 0 ? GRAPH_ROW_BK : GRAPH_ROW_BK_ALTERNATE}
        key={`refarea${i}`}
        // shape={
        //   <CustomReferenceArea
        //     dontExtend={false}
        //     //padding={X_AXIS_PADDING}
        //     padding={4}
        //   />
        // }
      />
    ));
  };

  const drawInvisibleLineForTooltip = () => {
    const allData = GetSeries_All(chartData);
    //console.log(allData);
    return <Line tooltipType="none" name="P0" data={allData} dataKey="y" visibility="hidden" />;
  };

  const drawLineSeries = () => {
    return props.dataSeries.map((ds, index) => (
      <Line
        key={`Line_${ds.name}`}
        type="monotone"
        tooltipType="none"
        name={ds.name}
        stroke={ds.color}
        data={GetSeries(chartData, index)}
        dataKey="y"
        connectNulls
        dot={{
          r:
            chartData.length > CONDENSED_VIEW_THRESHOLD || forceCondensed
              ? GRAPH_DOT_RADIUS_CONDENSED
              : GRAPH_DOT_RADIUS,
        }}
        activeDot={{ r: GRAPH_ACTIVE_DOT_RADIUS, cursor: props.onCategoryClicked ? 'pointer' : 'default' }}
      />
    ));
  };

  const drawBarSeries = () => {
    return props.dataSeries.map((ds, index) => {
      const data = GetSeries(chartData, index);
      return (
        <Bar
          key={`Bar_${ds.name}`}
          tooltipType="none"
          name={ds.name}
          label={
            props.labelFormatter ? (
              <CustomizedLabel
                dsIndex={index}
                formatter={props.labelFormatter}
                vertical
                dataSeries={props.dataSeries}
              />
            ) : (
              undefined
            )
          }
          stroke={ds.color}
          fill={ds.color}
          data={data}
          dataKey="y"
          radius={verticalBarRadius}
          cursor={props.onCategoryClicked ? 'pointer' : 'default'}
          // dataKey={`values[${index}]`}
        >
          {data.map((entry, index) => {
            if (props.colorPalette) {
              return (
                <Cell
                  key={`cell-${index}`}
                  fill={props.colorPalette?.interpolate(index / data.length)}
                  stroke={props.colorPalette?.interpolate(index / data.length)}
                />
              );
            }
            return <Cell key={`cell-${index}`} />;
          })}
        </Bar>
      );
    });
  };

  const drawStackedBarSeries = () => {
    return props.dataSeries.map((ds, index) => {
      return (
        <Bar
          key={`StackedBar_${ds.name}`}
          name={ds.name}
          stroke={ds.color}
          fill={ds.color}
          data={chartData}
          dataKey={ds.name}
          stackId="stackId"
          radius={index === props.dataSeries.length - 1 ? verticalBarRadius : undefined}
          cursor={props.onCategoryClicked ? 'pointer' : 'default'}
        />
      );
    });
  };

  const drawHorizontalBarSeries = () => {
    return props.dataSeries.map((ds, index) => {
      const data = GetSeries(chartData, index);
      return (
        <Bar
          key={`Bar_${ds.name}`}
          tooltipType="none"
          name={ds.name}
          label={
            props.labelFormatter ? (
              <CustomizedLabel dsIndex={index} formatter={props.labelFormatter} dataSeries={props.dataSeries} />
            ) : (
              undefined
            )
          }
          stroke={ds.color}
          fill={ds.color}
          data={data}
          dataKey="y"
          radius={horizontalBarRadius}
          cursor={props.onCategoryClicked ? 'pointer' : 'default'}
          // dataKey={`values[${index}]`}
        >
          {data.map((entry, index) => {
            if (props.colorPalette) {
              return (
                <Cell
                  key={`cell-${index}`}
                  fill={props.colorPalette?.interpolate(index / data.length)}
                  stroke={props.colorPalette?.interpolate(index / data.length)}
                />
              );
            }
            return <Cell key={`cell-${index}`} />;
          })}
        </Bar>
      );
    });
  };

  const drawHorizontalStackedBarSeries = () => {
    return props.dataSeries.map((ds, index) => {
      return (
        <Bar
          key={`StackedBar_${ds.name}`}
          name={ds.name}
          stroke={ds.color}
          fill={ds.color}
          data={chartData}
          dataKey={ds.name}
          stackId="stackId"
          radius={index === props.dataSeries.length - 1 ? horizontalBarRadius : undefined}
          cursor={props.onCategoryClicked ? 'pointer' : 'default'}
        />
      );
    });
  };

  const drawAreaSeries = () => {
    return props.dataSeries.map((ds, index) => (
      <Area
        key={`Area_${ds.name}`}
        type="monotone"
        tooltipType="none"
        name={ds.name}
        stroke={ds.color}
        strokeWidth={2}
        fill={ds.color}
        fillOpacity="10%"
        data={GetSeries(chartData, index)}
        dataKey="y"
        connectNulls
        dot={{
          r:
            chartData.length > CONDENSED_VIEW_THRESHOLD || forceCondensed
              ? GRAPH_DOT_RADIUS_CONDENSED
              : GRAPH_DOT_RADIUS,
          fill: 'white',
          fillOpacity: 1,
          stroke: ds.color,
        }}
        activeDot={{ r: GRAPH_ACTIVE_DOT_RADIUS, cursor: props.onCategoryClicked ? 'pointer' : 'default' }}
      />
    ));
  };

  const drawStackedAreaSeries = () => {
    return props.dataSeries.map((ds, index) => {
      return (
        <Area
          key={`StackedArea_${ds.name}`}
          type="monotone"
          tooltipType="none"
          name={ds.name}
          stroke={ds.color}
          strokeWidth={2}
          fill={ds.color}
          fillOpacity="10%"
          // data={GetSeries(chartData, index)}
          // dataKey="y"
          connectNulls
          dot={{
            r:
              chartData.length > CONDENSED_VIEW_THRESHOLD || forceCondensed
                ? GRAPH_DOT_RADIUS_CONDENSED
                : GRAPH_DOT_RADIUS,
            fill: 'white',
            fillOpacity: 1,
            stroke: ds.color,
          }}
          activeDot={{ r: GRAPH_ACTIVE_DOT_RADIUS }}
          data={chartData}
          dataKey={ds.name}
          stackId="stackId"
        />
      );
    });
  };

  // const renderPieCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index }) => {
  //  const RADIAN = Math.PI / 180;
  //   const radius = outerRadius * 1.2;
  //   const x = cx + radius * Math.cos(-midAngle * RADIAN);
  //   const y = cy + radius * Math.sin(-midAngle * RADIAN);

  //   return (
  //     <text x={x} y={y} fill="red" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central">
  //       {`${(percent * 100).toFixed(0)}%`}
  //     </text>
  //   );
  // };

  const renderStackedPie180FirstLabel = props => {
    const { cx, cy, startAngle, midAngle, middleRadius, outerRadius, percent, index, payload } = props;
    if (index !== 0) return null;
    // console.log(props);

    const RADIAN = Math.PI / 180;
    const x = cx + middleRadius * Math.cos(-startAngle * RADIAN);
    const y = cy + 5; //  + radius * Math.sin(-midAngle * RADIAN);

    return (
      <g transform={`translate(${x},${y})`}>
        {/* <circle cx={0} cy={0} r={5} fill="red" /> */}
        <text
          textAnchor="start"
          dominantBaseline="central"
          // transform="translate(100,100) rotate(90)"
          transform={`translate(${0},${0}) rotate(90)`}
        >
          {payload.catName}
        </text>
      </g>
    );
  };

  const drawPieSeries = () => {
    const dss = [...props.dataSeries];
    //dss.push(dss[0]);

    const radiuses = [];
    if (dss.length === 1) {
      radiuses.push({ innerRadius: undefined, outerRadius: undefined });
    } else {
      const minRadius = 0;
      const maxRadius = 80;
      for (let i = 0; i < dss.length; i++) {
        radiuses.push({
          innerRadius: `${(i * maxRadius) / dss.length + 0}%`,
          outerRadius: `${((i + 1) * maxRadius) / dss.length - 1}%`,
        });
      }
    }
    // console.log('radiuses', radiuses);

    return dss.map((ds, i) => {
      // const data = i === 0 ? GetSeries(chartData, 0) : GetSeries(chartData, 0).filter(d => d.x !== 'Offline').concat([{x: 'Off A', y: 20}, {x: 'Off B', y: 18}, {x: 'Off C', y: 7}, {x: 'Off D', y: 3}, {x: 'Off E', y: 1 }]);
      const data = GetSeries(chartData, i);
      // console.log('data', data);
      return (
        <Pie
          // activeIndex={activeIndex}
          // activeShape={renderActiveShape}
          // innerRadius="86%"
          // outerRadius="100%"
          allowDuplicatedCategory={false}
          key={`Pie_${i}_${ds.name}`}
          data={data}
          dataKey="y"
          fill={ds.color}
          startAngle={90}
          endAngle={-270}
          label
          innerRadius={radiuses[i].innerRadius}
          outerRadius={radiuses[i].outerRadius}
          // label = {renderPieCustomizedLabel}
          // innerRadius={'70%'}
          // minAngle={5}
          // onMouseEnter={(data, index) => setActiveIndex(index)}
          // onMouseLeave={() => setActiveIndex(0)}

          onClick={nextState => {
            if (nextState?.x && props.onCategoryClicked) props.onCategoryClicked(nextState.x);
          }}
        >
          {data.map((_, index) => (
            <Cell
              key={`cell-${index}`}
              fill={props.colorPalette?.interpolate(index / data.length)}
              cursor={props.onCategoryClicked ? 'pointer' : 'default'}
            />
          ))}
        </Pie>
      );
    });
  };

  const calcRadiuses = levels => {
    const radiuses = [];
    if (levels === 1) {
      radiuses.push({ innerRadius: undefined, outerRadius: undefined });
    } else {
      const minRadius = 0;
      const maxRadius = 110;
      for (let i = 0; i < levels; i++) {
        radiuses.push({
          innerRadius: `${(i * maxRadius) / levels + 0}%`,
          outerRadius: `${((i + 1) * maxRadius) / levels - 1}%`,
        });
      }
    }

    return radiuses;
  };

  const drawStackedPieSeries = () => {
    const dss = [...props.dataSeries];
    const catsCount = chartData.length;
    const radiuses = calcRadiuses(catsCount);

    const sums = chartData.map(cd => dss.reduce((sum, ds) => sum + cd[ds.name], 0));
    const maxSum = Math.max(...sums);
    const startAngle = 180;
    const maxAngle = 180;
    // const startAngle = 90;
    // const maxAngle = 360;
    const angles = sums.map(sum => (sum / maxSum) * maxAngle);

    return Array.from({ length: catsCount }).map((_, i) => {
      const data = GetSeries_Cat(dss, chartData, i);

      //console.log('data', data);

      return (
        <Pie
          allowDuplicatedCategory={false}
          key={`Pie_${i}`}
          data={data}
          dataKey="y"
          startAngle={startAngle}
          endAngle={startAngle - angles[i]}
          label={renderStackedPie180FirstLabel}
          labelLine={false}
          innerRadius={radiuses[i].innerRadius}
          outerRadius={radiuses[i].outerRadius}
          onClick={nextState => {
            if (nextState?.x && props.onCategoryClicked) props.onCategoryClicked(nextState.x);
          }}
          cy="75%"
        >
          {dss.map((ds, index) => (
            <Cell key={`cell-${index}`} fill={ds.color} cursor={props.onCategoryClicked ? 'pointer' : 'default'} />
          ))}
        </Pie>
      );
    });
  };

  const drawHierarchicalPieSeries = () => {
    const dss = [...props.dataSeries];
    const levelsCount = 2;
    const radiuses = calcRadiuses(levelsCount);

    const sum1 = chartData.filter(cd => !cd.parent).reduce((sum, cd) => sum + cd[dss[0].name] + cd[dss[1].name], 0);
    const sum2 = chartData.filter(cd => !!cd.parent).reduce((sum, cd) => sum + cd[dss[0].name] + cd[dss[1].name], 0);
    const maxSum = Math.max(sum1, sum2);
    const startAngle = 180;
    const maxAngle = -180;
    // const startAngle = 90;
    // const maxAngle = 360;
    const angles = [
      { startAngle, endAngle: startAngle + maxAngle },
      { startAngle: startAngle + maxAngle * (1 - sum2 / maxSum), endAngle: startAngle + maxAngle },
    ];

    //console.log(sum1, sum2, chartData, angles);

    const data1 = GetSeries_Conditional(dss, chartData, cd => !cd.parent, props.layerNames[0], 0);
    const data2 = GetSeries_Conditional(dss, chartData, cd => !!cd.parent, props.layerNames[1], 1, data1);
    const datas = [data1, data2];

    return datas.map((data, i) => (
      <Pie
        allowDuplicatedCategory={false}
        key={`Pie_${i}`}
        data={data}
        dataKey="y"
        startAngle={angles[i].startAngle}
        endAngle={angles[i].endAngle}
        label={renderStackedPie180FirstLabel}
        labelLine={false}
        innerRadius={radiuses[i].innerRadius}
        outerRadius={radiuses[i].outerRadius}
        onClick={nextState => {
          if (nextState?.x && props.onCategoryClicked) props.onCategoryClicked(nextState.x);
        }}
        cy="65%"
      >
        {data.map((d, index) => (
          <Cell key={`cell-${index}`} fill={d.color} cursor={props.onCategoryClicked ? 'pointer' : 'default'} />
        ))}
      </Pie>
    ));
  };

  const drawReferenceLines = () => {
    return referenceLines.map(rl => (
      <ReferenceLine key={rl} y={rl} label="" stroke={GRAPH_REF_LINE_COLOR} strokeDasharray={GRAPH_REF_LINE_DASH} />
    ));
  };

  const drawLegendFromDataSeries = () => {
    return (
      <Legend
        layout="horizontal"
        verticalAlign="bottom"
        payload={dataSeries.map((ds, index) => ({
          id: ds.name,
          // type: 'square',
          value: `${ds.name}`,
          color: ds.color,
        }))}
      />
    );
  };

  const drawHierarchicalLegend = () => {
    const data = chartData.filter(d => !d.parent);
    const entries = [];
    data.forEach((d, index) => {
      dataSeries.forEach(ds => {
        entries.push({
          label: d.x + ' - ' + ds.name,
          color: ds.colors[index],
        });
      });
    });

    return (
      <Legend
        layout="horizontal"
        verticalAlign="bottom"
        payload={entries.map(e => ({
          id: e.label,
          // type: 'square',
          value: e.label,
          color: e.color,
        }))}
      />
    );
  };

  const defaultChartMargin = {
    top: 5,
    right: 10,
    bottom: 5,
    left: 10,
  };

  const drawLineChart = () => {
    // console.log('LINE CHART');
    // console.log('chartData', chartData);
    // console.log(valMinMax, valDelta, tick, yMin, yMax, yTicksCount, yTicks);

    return (
      <LineChart
        data={chartData}
        defaultShowTooltip={false}
        margin={defaultChartMargin}
        onClick={(nextState, _) => {
          if (nextState?.activeLabel && props.onCategoryClicked) props.onCategoryClicked(nextState.activeLabel);
        }}
      >
        {drawXAxis()}
        {drawCategoryTooltip()}
        {hasReferenceAreas && drawAlternateRows()}
        {drawYAxis(useZeroAsMin)}

        {drawLineSeries()}

        {referenceLines && drawReferenceLines()}

        {props.dataSeries?.length > 1 && (
          <Legend
            layout="horizontal"
            verticalAlign="bottom"
            // formatter={(_, entry) => <span>{entry.payload.x}</span>}
          />
        )}
      </LineChart>
    );
  };

  const drawBarChart = () => {
    // console.log('BAR CHART');
    // console.log('chartData', chartData);
    // console.log(valMinMax, valDelta, tick, yMin, yMax, yTicksCount, yTicks);

    return (
      <BarChart
        data={chartData}
        defaultShowTooltip={false}
        margin={defaultChartMargin}
        layout={graphType === chartTypeEnum.horizontalBar ? 'vertical' : 'horizontal'}
        onClick={(nextState, _) => {
          if (nextState?.activeLabel && props.onCategoryClicked) props.onCategoryClicked(nextState.activeLabel);
        }}
      >
        {drawXAxis()}
        {hasReferenceAreas && drawAlternateRows()}
        {drawTooltip()}
        {drawYAxis(useZeroAsMin)}

        {drawBarSeries()}

        {referenceLines && drawReferenceLines()}
      </BarChart>
    );
  };

  const drawStackedBarChart = () => {
    // console.log('STACKED BAR CHART');
    // console.log('graphData', graphData);
    // console.log('chartData', chartData);

    return (
      <BarChart
        data={chartData}
        margin={defaultChartMargin}
        onClick={(nextState, _) => {
          if (nextState?.activeLabel && props.onCategoryClicked) props.onCategoryClicked(nextState.activeLabel);
        }}
      >
        {hasReferenceAreas && drawAlternateRows()}

        {drawXAxis()}
        {drawYAxis(useZeroAsMin)}
        {drawCategoryTooltip()}
        {drawStackedBarSeries()}

        {referenceLines && drawReferenceLines()}

        {props.hasLegend && (
          <Legend
            layout="horizontal"
            verticalAlign="bottom"
            // formatter={(_, entry) => <span>{entry.payload.x}</span>}
          />
        )}
      </BarChart>
    );
  };

  const drawHorizontalBarChart = () => {
    // console.log('HORIZONTAL BAR CHART');
    // console.log('chartData', chartData);
    // console.log(valMinMax, valDelta, tick, yMin, yMax, yTicksCount, yTicks);

    return (
      <BarChart
        data={chartData}
        defaultShowTooltip={false}
        margin={{
          top: 5,
          right: 10,
          bottom: 5,
          left: 10,
        }}
        layout="vertical"
        onClick={nextState => {
          if (nextState && nextState.activeLabel && props.onCategoryClicked)
            props.onCategoryClicked(nextState.activeLabel);
        }}
      >
        {drawXAxis_Horizontal(useZeroAsMin)}
        {hasReferenceAreas && drawAlternateCols()}
        {drawTooltip()}
        {drawYAxis_Horizontal()}

        {drawHorizontalBarSeries()}

        {/* {referenceLines && drawReferenceLines()} */}
      </BarChart>
    );
  };

  const drawHorizontalStackedBarChart = () => {
    // console.log('STACKED BAR CHART');
    // console.log('graphData', graphData);
    // console.log('chartData', chartData);

    return (
      <BarChart
        data={chartData}
        margin={{
          top: 5,
          right: 10,
          bottom: 5,
          left: 10,
        }}
        layout="vertical"
        onClick={(nextState, _) => {
          if (nextState && nextState.activeLabel && props.onCategoryClicked)
            props.onCategoryClicked(nextState.activeLabel);
        }}
      >
        {hasReferenceAreas && drawAlternateCols()}

        {drawXAxis_Horizontal(useZeroAsMin)}
        {drawYAxis_Horizontal()}
        {drawCategoryTooltip()}
        {drawHorizontalStackedBarSeries()}

        {props.hasLegend && (
          <Legend
            layout="horizontal"
            verticalAlign="bottom"
            // formatter={(_, entry) => <span>{entry.payload.x}</span>}
          />
        )}

        {/* {referenceLines && drawReferenceLines()} */}
      </BarChart>
    );
  };

  const drawAreaChart = () => {
    return (
      <ComposedChart
        data={chartData}
        defaultShowTooltip={false}
        margin={defaultChartMargin}
        onClick={(nextState, _) => {
          if (nextState?.activeLabel && props.onCategoryClicked) props.onCategoryClicked(nextState.activeLabel);
        }}
      >
        {drawXAxis()}
        {drawTooltip()}
        {hasReferenceAreas && drawAlternateRows()}
        {drawYAxis(useZeroAsMin)}

        {/* area chart specific */}
        {drawAreaSeries()}

        {referenceLines && drawReferenceLines()}
      </ComposedChart>
    );
  };

  const drawStackedAreaChart = () => {
    return (
      <ComposedChart
        data={chartData}
        defaultShowTooltip={false}
        margin={defaultChartMargin}
        onClick={(nextState, _) => {
          if (nextState?.activeLabel && props.onCategoryClicked) props.onCategoryClicked(nextState.activeLabel);
        }}
      >
        {drawXAxis()}
        {drawCategoryTooltip()}
        {hasReferenceAreas && drawAlternateRows()}
        {drawYAxis(useZeroAsMin)}

        {drawStackedAreaSeries()}

        {referenceLines && drawReferenceLines()}

        <Legend
          layout="horizontal"
          verticalAlign="bottom"
          // formatter={(_, entry) => <span>{entry.payload.x}</span>}
        />
      </ComposedChart>
    );
  };

  const drawPieChart = () => {
    return (
      <PieChart data={chartData} defaultShowTooltip={false} allowDuplicatedCategory={false} margin={defaultChartMargin}>
        {drawTooltip()}
        {drawPieSeries()}

        <Legend
          // layout="vertical"
          // align="right"
          // verticalAlign="middle"
          layout="horizontal"
          verticalAlign="bottom"
          formatter={(_, entry) => <span>{entry.payload.x}</span>}
        />
      </PieChart>
    );
  };

  const drawStackedPieChart = () => {
    return (
      <PieChart data={chartData} defaultShowTooltip={false} allowDuplicatedCategory={false} margin={defaultChartMargin}>
        {drawCategoryTooltip()}
        {drawStackedPieSeries()}
        {drawLegendFromDataSeries()}
      </PieChart>
    );
  };

  const drawHierarchicalPieChart = () => {
    return (
      <PieChart data={chartData} defaultShowTooltip={false} allowDuplicatedCategory={false} margin={defaultChartMargin}>
        {drawHierarchicalTooltip()}
        {drawHierarchicalPieSeries()}
        {drawHierarchicalLegend()}
      </PieChart>
    );
  };

  const renderResponsiveChart = chart => (
    <div ref={containerRef} className={hasBorder ? 'graphDiv' : 'graphDivNoBorder'}>
      <ResponsiveContainer width="100%" height="99%" debounce>
        {chart}
      </ResponsiveContainer>
    </div>
  );

  if (graphType === chartTypeEnum.line) {
    return renderResponsiveChart(drawLineChart());
  }

  if (graphType === chartTypeEnum.bar) {
    return renderResponsiveChart(drawBarChart());
  }

  if (graphType === chartTypeEnum.stackedBar) {
    return renderResponsiveChart(drawStackedBarChart());
  }

  if (graphType === chartTypeEnum.horizontalBar) {
    return renderResponsiveChart(drawHorizontalBarChart());
  }

  if (graphType === chartTypeEnum.horizontalStackedBar) {
    return renderResponsiveChart(drawHorizontalStackedBarChart());
  }

  if (graphType === chartTypeEnum.area) {
    return renderResponsiveChart(drawAreaChart());
  }

  if (graphType === chartTypeEnum.stackedArea) {
    return renderResponsiveChart(drawStackedAreaChart());
  }

  if (graphType === chartTypeEnum.pie) {
    return renderResponsiveChart(drawPieChart());
  }

  if (graphType === chartTypeEnum.stackedPie) {
    return renderResponsiveChart(drawStackedPieChart());
  }

  if (graphType === chartTypeEnum.hierarchicalPie) {
    return renderResponsiveChart(drawHierarchicalPieChart());
  }
}

export default React.memo(
  GenericCategoryGraph,
  (prevProps, nextProps) =>
    _.isEqual(prevProps.graphData, nextProps.graphData) && _.isEqual(prevProps.dataSeries, nextProps.dataSeries),
);

GenericCategoryGraph.propTypes = {
  graphData: PropTypes.array,
  hasBorder: PropTypes.bool,
  forceCondensed: PropTypes.bool,
  xAxisTopPosition: PropTypes.bool,
  xAxisLabelsMinMaxOnly: PropTypes.bool,
  yAxisWidth: PropTypes.number,
  yAxisUnit: PropTypes.string,
  yAxisAlign: PropTypes.string,
  tooltipTitle: PropTypes.string,
  tooltipContentLines: PropTypes.func,
  tooltipDateFormat: PropTypes.string,
  dataSeries: PropTypes.array,
  referenceLines: PropTypes.array,
  categorySelector: PropTypes.func,
  hasReferenceAreas: PropTypes.bool,
  graphType: PropTypes.string,
  onCategoryClicked: PropTypes.func,
  labelFormatter: PropTypes.func,
  xAxisType: PropTypes.string,
  barRadius: PropTypes.oneOfType([PropTypes.number, PropTypes.arrayOf(PropTypes.number)]),
  horizontalBarRadius: PropTypes.oneOfType([PropTypes.number, PropTypes.arrayOf(PropTypes.number)]),
};
